import { Typography, Stack, CircularProgress, Alert } from '@mui/material';
import { useMemo } from 'react';
import { useAppSelector } from '../../../redux/store';
import { useIsMobile } from '../../../hooks/useResponsive';
import { ClockRecord } from '../../../components/ClockRecord';
import { CsvExtractorButton } from './CsvExtractorButton';
import { IncorrectTimeNotesTable } from './IncorrectTimeNotesTable';

const dangerPeriodDays = 14;

export const ClockContent: React.FC<{}> = () => {
  const isMob = useIsMobile();
  const { report, product, combineIssues } = useAppSelector((s) => s.product);
  const { from, to } = useAppSelector((s) => s.range);
  const isSmallAmount = useMemo(() => {
    if (!from || !to || !product) return undefined;
    const days = (to.getTime() - from.getTime()) / 1000 / 60 / 60 / 24;
    if (days > dangerPeriodDays)
      return 'Dangerously big period! Results can be incorrect';
    return undefined;
  }, [from, to, product]);
  const csvAvailable = useMemo(() => {
    return !combineIssues && !report.loading && report.data.length > 0;
  }, [report.data, combineIssues, report.loading]);
  return (
    <>
      {report.error && <Alert severity="error">{report.error}</Alert>}
      {isSmallAmount && <Alert severity="warning">{isSmallAmount}</Alert>}
      {report.loading !== undefined && (
        <Stack
          direction="row"
          spacing={5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography variant="h3">{report.loading}</Typography>
        </Stack>
      )}
      {report.dangerIssues.length > 0 && (
        <Alert severity="error">
          Dangerous issues found:{' '}
          {
            <>
              {report.dangerIssues.map((i) => (
                <>
                  <br />
                  <a href={i.webUrl} target="_blank" rel="noopener noreferrer">
                    {i.title}
                  </a>
                </>
              ))}
            </>
          }
        </Alert>
      )}
      {report.incorrectTimeNotes.length > 0 && (
        <>
          <Alert severity="warning">
            Possible incorrect time notes found: {report.incorrectTimeNotes.length}
          </Alert>
          <IncorrectTimeNotesTable incorrectNotes={report.incorrectTimeNotes} />
        </>
      )}
      {csvAvailable && (
        <CsvExtractorButton productName={product?.name ?? '-'} reportData={report.data} />
      )}
      {report.data.map((record) => (
        <ClockRecord
          key={record.author.username}
          record={record}
          isMob={isMob ?? false}
          highlightDefault
        />
      ))}
    </>
  );
};
